import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["id", "color", "role", "a11yLabel", "iconWhenFull", "iconWhenEmpty", "dimmed", "value", "scale", "tooltip"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import "core-js/modules/es.symbol.js";
import "core-js/modules/es.array.concat.js";
import "core-js/modules/es.array.filter.js";
import "core-js/modules/es.array.flat.js";
import "core-js/modules/es.array.includes.js";
import "core-js/modules/es.array.unscopables.flat.js";
import "core-js/modules/es.object.get-own-property-descriptor.js";
import "core-js/modules/es.object.get-own-property-descriptors.js";
import "core-js/modules/es.object.keys.js";
import "core-js/modules/es.object.to-string.js";
import "core-js/modules/es.regexp.to-string.js";
import "core-js/modules/web.dom-collections.for-each.js";
import React from 'react';
import styled, { useTheme } from 'styled-components';
import { fillParent } from 'styles/mixins';
import { contrast, getBorderColor, getDimmedTextColor, getGlobalThemeValue, pxToEm } from 'utils/ui';
import Box from 'components/Box';
import VisuallyHidden from 'components/VisuallyHidden';
import Icon from 'components/Icon';
import EasyTooltip from 'components/EasyTooltip';
import useUniqueId from 'hooks/useUniqueId';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
function getRotation(_ref) {
  var value = _ref.value,
    right = _ref.right;
  var multiplier = value;
  if (!right) {
    if (value < 0.5) {
      return '180deg';
    }
    multiplier = value - 0.5;
  }
  return "".concat(Math.min(180 + multiplier * 360, 360), "deg");
}
var Wrapper = styled(Box).withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['scale', 'dimmedBorder', 'textColor'].includes(prop);
  },
  displayName: "ProgressCircle__Wrapper",
  componentId: "sc-1o176xd-0"
})(function (p) {
  var borderColor = p.dimmedBorder ? getBorderColor(p.theme) : getGlobalThemeValue('colorBlocks', p.textColor)(p.theme);
  var borderSize = pxToEm(2, 24);
  return "\n\t\tposition: relative;\n\t\tvertical-align: middle;\n\t\tdisplay: inline-grid;\n\t\tgrid-template-columns: 50% 50%;\n\t\twidth: 1em;\n\t\theight: 1em;\n\t\toverflow: hidden;\n\n\t\t/* Repeated declaration for browsers that don't support round() */\n\t\tfont-size: calc(".concat(p.scale, " * 1.5rem);\n\t\tfont-size: round(calc(").concat(p.scale, " * 1.5rem), 1px);\n\t\tborder-radius: 100%;\n\n\t\t/* Repeated declaration for browsers that don't support round() */\n\t\tbox-shadow: inset 0 0 0 ").concat(borderSize, " ").concat(borderColor, ";\n\t\tbox-shadow: inset 0 0 0 round(").concat(borderSize, ", 1px) ").concat(borderColor, ";\n\t");
});

// We're centreing the logo using rounded %-based margins
// for higher visual consistency.
// Using flexbox-based centring would lead to the icon position
// to vary slightly from one component instance to the next

var CenteredIcon = styled(Icon).withConfig({
  displayName: "ProgressCircle__CenteredIcon",
  componentId: "sc-1o176xd-1"
})(["grid-row:1;grid-column:1 / -1;z-index:", ";margin:12.5%;margin:round(down,12.5%,1px);margin-block:round(up,12.5%,1px);"], function (p) {
  return p.theme.globals.z.flat + 1;
});
var InnerHalf = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['right', 'value'].includes(prop);
  }
}).withConfig({
  displayName: "ProgressCircle__InnerHalf",
  componentId: "sc-1o176xd-2"
})(["position:relative;width:100%;grid-row:1;grid-column:", ";overflow:hidden;&::before{content:'';", " background-color:currentColor;transform-origin:center ", ";transform:rotate(", ");}"], function (p) {
  return p.right ? 2 : 1;
}, fillParent, function (p) {
  return p.right ? 'left' : 'right';
}, getRotation);
function ProgressCircle(_ref2) {
  var id = _ref2.id,
    _ref2$color = _ref2.color,
    color = _ref2$color === void 0 ? 'positive' : _ref2$color,
    _ref2$role = _ref2.role,
    role = _ref2$role === void 0 ? 'progressbar' : _ref2$role,
    a11yLabel = _ref2.a11yLabel,
    iconWhenFull = _ref2.iconWhenFull,
    iconWhenEmpty = _ref2.iconWhenEmpty,
    dimmed = _ref2.dimmed,
    value = _ref2.value,
    _ref2$scale = _ref2.scale,
    scale = _ref2$scale === void 0 ? 1 : _ref2$scale,
    _ref2$tooltip = _ref2.tooltip,
    tooltip = _ref2$tooltip === void 0 ? 'auto' : _ref2$tooltip,
    otherProps = _objectWithoutProperties(_ref2, _excluded);
  var labelId = useUniqueId(id === null || id === void 0 ? void 0 : id.toString());
  var a11yProps;
  if (role !== 'none') {
    a11yProps = {
      role: role,
      'aria-valuenow': value * 100,
      'aria-labelledby': labelId
    };
  }
  var theme = useTheme();
  var circleColor = getGlobalThemeValue('colorBlocks', color)(theme);
  var iconToDisplay;
  var iconColor;
  if (value === 1 && iconWhenFull) {
    iconToDisplay = iconWhenFull;
    iconColor = contrast(circleColor);
  }
  if (value === 0 && iconWhenEmpty) {
    iconToDisplay = iconWhenEmpty;
    iconColor = getDimmedTextColor(theme);
  }
  return /*#__PURE__*/_jsx(EasyTooltip, {
    content: tooltip === 'auto' ? a11yLabel : tooltip,
    children: /*#__PURE__*/_jsxs(Wrapper, _objectSpread(_objectSpread(_objectSpread({
      as: "span"
    }, otherProps), a11yProps), {}, {
      textColor: color,
      dimmedBorder: value === 0 && dimmed,
      scale: scale,
      children: [iconToDisplay && /*#__PURE__*/_jsx(CenteredIcon, {
        name: iconToDisplay,
        "data-iconname": iconToDisplay,
        scale: 0.6667,
        color: iconColor
      }), value > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(InnerHalf, {
          right: false,
          value: value
        }), /*#__PURE__*/_jsx(InnerHalf, {
          right: true,
          value: value
        })]
      }), /*#__PURE__*/_jsx(VisuallyHidden, {
        id: labelId,
        children: a11yLabel
      })]
    }))
  });
}
export default ProgressCircle;